import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * DEV-ONLY mock interceptor for the genetic-report upload journey.
 *
 * It lets you preview the whole upload → processing → outcome flow on
 * localhost without a real backend or Azure Blob Storage. It is OPT-IN and
 * never touches traffic unless you explicitly turn it on, so colleagues with a
 * working backend are unaffected.
 *
 * Activate from the browser URL (the value is persisted to localStorage):
 *   ?geneticsDemo=review      → upload settles to "Under review" (default)
 *   ?geneticsDemo=more-info   → upload settles to "More info required" (error card)
 *   ?geneticsDemo=confirmed   → upload settles to a confirmed mutation
 *   ?geneticsDemo=confirmed-multi → confirmed with two mutations (multi-finding card)
 *   ?geneticsDemo=reset       → keep the scenario but allow re-running an upload
 *   ?geneticsDemo=off         → fully disable the mock
 *
 * Typical flow: open a DRAFT patient with e.g. `?geneticsDemo=review`, click
 * "Upload genetic report", pick any file, and watch the passport advance.
 *
 * Everything that is NOT part of the upload flow (patient load, master tables,
 * surveys, ...) passes straight through to the real backend untouched.
 */

const SCENARIOS = ['review', 'more-info', 'confirmed', 'confirmed-multi'] as const;
type Scenario = (typeof SCENARIOS)[number];

const SCENARIO_KEY = 'geneticsDemo';
const UPLOADED_KEY = 'geneticsDemoUploaded';
const MOCK_BLOB_HOST = 'genetics-demo.mock';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Reads the active scenario, applying any `?geneticsDemo=` URL override first
 * and persisting it so it survives polling and reloads.
 */
function activeScenario(): Scenario | null {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has(SCENARIO_KEY)) {
      const raw = (params.get(SCENARIO_KEY) || '').toLowerCase();
      if (raw === 'off' || raw === '') {
        safeRemove(SCENARIO_KEY);
        safeRemove(UPLOADED_KEY);
      } else if (raw === 'reset') {
        safeRemove(UPLOADED_KEY);
      } else if (raw === 'on') {
        safeSet(SCENARIO_KEY, 'review');
      } else {
        safeSet(SCENARIO_KEY, raw);
      }
    }
  } catch {
    /* ignore */
  }

  const stored = (safeGet(SCENARIO_KEY) || '').toLowerCase();
  if (!stored) return null;
  return ((SCENARIOS as readonly string[]).includes(stored) ? stored : 'review') as Scenario;
}

function rettxidFrom(pattern: RegExp, url: string): string | null {
  const m = url.match(pattern);
  return m ? m[1] : null;
}

function confirmedMutation() {
  return {
    id: 'mock-mut-1',
    mutationId: 'mock-mut-1',
    validationStatus: 'validated',
    mutation_data: {
      genome_assembly: 'GRCh38',
      genomic_coordinate: 'NC_000023.11:g.154030912G>A',
      genomic_coordinates: [
        {
          assembly: 'GRCh38',
          hgvs: 'NC_000023.11:g.154030912G>A',
          start: 154030912,
          end: 154030912,
          size: 1
        }
      ],
      variant_type: 'SNV',
      primary_transcript: {
        hgvs_transcript_variant: 'NM_004992.4:c.473C>T',
        protein_consequence_slr: 'NP_004983.1:p.(Arg158Gln)',
        protein_consequence_tlr: 'NP_004983.1:p.(Arg158Gln)'
      },
      secondary_transcript: null
    }
  };
}

/** A distinct second mutation, used by the `confirmed-multi` scenario. */
function secondMutation() {
  return {
    id: 'mock-mut-2',
    mutationId: 'mock-mut-2',
    validationStatus: 'validated',
    mutation_data: {
      genome_assembly: 'GRCh38',
      genomic_coordinate: 'NC_000023.11:g.154031237C>T',
      genomic_coordinates: [
        {
          assembly: 'GRCh38',
          hgvs: 'NC_000023.11:g.154031237C>T',
          start: 154031237,
          end: 154031237,
          size: 1
        }
      ],
      variant_type: 'SNV',
      primary_transcript: {
        hgvs_transcript_variant: 'NM_004992.4:c.916C>T',
        protein_consequence_slr: 'NP_004983.1:p.(Arg306Cys)',
        protein_consequence_tlr: 'NP_004983.1:p.(Arg306Cys)'
      },
      secondary_transcript: null
    }
  };
}

/**
 * Per-scenario overrides merged onto the real patient response after an upload,
 * so the passport advances coherently while keeping the patient's real fields.
 */
function patientOverrides(scenario: Scenario, body: Record<string, unknown>): Record<string, unknown> {
  const now = new Date().toISOString();
  const lifecycle = (body['lifecycle'] as Record<string, unknown>) || {};
  const capabilities = (body['capabilities'] as Record<string, unknown>) || {};

  if (scenario === 'confirmed' || scenario === 'confirmed-multi') {
    return {
      lifecycle: { ...lifecycle, state: 'CONFIRMED', updated_at: now },
      capabilities: {
        ...capabilities,
        edit: { allowed: false },
        delete: { allowed: false },
        upload_diagnosis: { allowed: false },
        request_correction: { allowed: true }
      },
      main_diagnosis: {
        diagnosis_name: 'Rett Syndrome (MECP2)',
        diagnosis_type: 'genetic',
        clinical_diagnosis: false
      },
      mutations: scenario === 'confirmed-multi'
        ? [confirmedMutation(), secondMutation()]
        : [confirmedMutation()],
      mutation_summary: { validation: { status_code: 'valid-mutation' } }
    };
  }

  if (scenario === 'more-info') {
    return {
      lifecycle: { ...lifecycle, state: 'MORE_INFO_REQUIRED', updated_at: now },
      capabilities: {
        ...capabilities,
        edit: { allowed: true },
        upload_diagnosis: { allowed: true }
      },
      mutation_summary: {
        validation: {
          status_code: 'missing-mutation-in-report',
          description_localized:
            'No pudimos identificar la mutación en el informe. Sube un informe que incluya la variante de MECP2.',
          description: 'We could not identify the mutation in the report. Please upload a report that includes the MECP2 variant.'
        }
      }
    };
  }

  // review (default)
  return {
    lifecycle: { ...lifecycle, state: 'UNDER_REVIEW', updated_at: now },
    capabilities: {
      ...capabilities,
      edit: { allowed: false },
      delete: { allowed: false },
      upload_diagnosis: { allowed: false }
    }
  };
}

export const mockGeneticsInterceptor: HttpInterceptorFn = (req, next) => {
  // Hard production guard — this code path can never run in prod builds.
  if (environment.production) {
    return next(req);
  }

  const scenario = activeScenario();
  if (!scenario) {
    return next(req);
  }

  const url = req.url;

  // 1) Upload token request → return a mock token pointing at the mock blob host.
  if (req.method === 'POST' && url.includes('upload-file-info')) {
    const rettxid = rettxidFrom(/files\/([^/]+)\/files\/upload-file-info/, url) || 'unknown';
    safeSet(UPLOADED_KEY, rettxid);
    const fileId = `mock-${Date.now()}`;
    return of(
      new HttpResponse({
        status: 200,
        body: {
          expiration: new Date(Date.now() + 3600_000).toISOString(),
          file_id: fileId,
          file_url: `https://${MOCK_BLOB_HOST}/blob/${fileId}`,
          patient_id: rettxid
        }
      })
    ).pipe(delay(600));
  }

  // 2) The actual blob PUT to the mock host → succeed silently.
  if (url.includes(MOCK_BLOB_HOST)) {
    return of(new HttpResponse({ status: 200, body: null })).pipe(delay(800));
  }

  // 3) Mutation status poll → always report the file as registered so the
  //    component stops polling and refreshes the patient (which we then patch).
  if (req.method === 'GET' && url.includes('mutations-status')) {
    return of(
      new HttpResponse({
        status: 200,
        body: {
          state: 'PENDING_VALIDATION',
          validation_status: null,
          allow_upload: false,
          show_instructions: false,
          show_status: true,
          show_mutation_list: false,
          last_file_id: safeGet(UPLOADED_KEY) || '',
          last_updated_at: new Date().toISOString(),
          validation_description: ''
        }
      })
    ).pipe(delay(400));
  }

  // 4) Patient GET refresh → patch the real response to the scenario outcome,
  //    but only for the patient that was just "uploaded" against.
  if (req.method === 'GET') {
    const rettxid = rettxidFrom(/\/patients\/([^/?]+)(?:\?.*)?$/, url);
    const uploadedId = safeGet(UPLOADED_KEY);
    if (rettxid && uploadedId && rettxid === uploadedId) {
      return next(req).pipe(
        map((event) => {
          if (event instanceof HttpResponse && event.body && typeof event.body === 'object') {
            const body = event.body as Record<string, unknown>;
            return event.clone({ body: { ...body, ...patientOverrides(scenario, body) } });
          }
          return event;
        }),
        delay(300)
      );
    }
  }

  return next(req);
};
